import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export interface FaqItem {
  question: string;
  answer: string;
  category: "General & Governance" | "Software & RaBuOS" | "Hardware & R&D" | "Training & AMC";
}

export const FAQ_LIST: FaqItem[] = [
  {
    category: "General & Governance",
    question: "What is RADIECE's statutory incorporation and operational status?",
    answer: "RADIECE PRIVATE LIMITED is an incorporated Indian deep-tech engineering company (CIN: U58200KL2026PTC104862, GSTIN: 32AAQCR1162B1ZR) headquartered at CSEZ P.O, Kakkanad, Ernakulam, Kerala. We operate in strict alignment with MCA statutory guidelines, the Digital Personal Data Protection (DPDP) Act 2023, and CERT-In cybersecurity directives.",
  },
  {
    category: "Software & RaBuOS",
    question: "What is RaBuOS and how is it deployed for enterprise clients?",
    answer: "RaBuOS (Radiece Business Operating System) is our proprietary enterprise operating platform engineered on the Bun and TypeScript runtime. It unifies MCA corporate filings, automated GST reconciliation, visual DAG task workflows, and real-time collaboration. It can be deployed as a multi-tenant cloud SaaS or as an isolated on-premise installation.",
  },
  {
    category: "Hardware & R&D",
    question: "What custom electronic and IoT hardware prototyping capabilities do you provide?",
    answer: "Our in-house R&D lab in Kakkanad designs custom electronic circuit schematics, multi-layer PCBs, deterministic firmware in C/C++ (FreeRTOS for ESP32 and ARM Cortex), and long-range wireless telemetry mesh networks using LoRaWAN (865–867 MHz ISM band) with end-to-end industrial calibration.",
  },
  {
    category: "General & Governance",
    question: "How does intellectual property (IP) and source code ownership work?",
    answer: "For bespoke software development and commissioned hardware R&D, 100% of the custom intellectual property, compiled firmware binaries, and designated source code repositories transfer directly to the client upon milestone settlement under our standard Master Service Agreement (MSA).",
  },
  {
    category: "Training & AMC",
    question: "What is included in RADIECE Annual Maintenance Contracts (AMC)?",
    answer: "Our AMC plans provide proactive quarterly system health audits, emergency zero-day security patching, dependency upgrades, database backup verification, 24/7 SLA infrastructure monitoring, and priority technical support.",
  },
  {
    category: "Training & AMC",
    question: "How are corporate and institutional training programs structured?",
    answer: "Our technical education tracks (Full-Stack Web, Embedded Systems & IoT, Cyber Security, and AI/ML) are taught by active systems architects. Programs emphasize hands-on laboratory milestones, hardware development kits, and real-world production engineering practices.",
  },
];

export function FaqSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const categories = ["All", "General & Governance", "Software & RaBuOS", "Hardware & R&D", "Training & AMC"];

  const filteredFaqs = activeCategory === "All"
    ? FAQ_LIST
    : FAQ_LIST.filter((f) => f.category === activeCategory);

  return (
    <section className="no-print bg-slate-50 dark:bg-[#070b12] py-16 sm:py-24 border-t border-slate-200/80 dark:border-slate-800/80 transition-colors">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 text-brand dark:text-primary text-xs font-semibold uppercase tracking-wider mb-3">
            Technical & Governance FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
            Frequently Asked Engineering Questions
          </h2>
          <p className="mt-3 text-slate-500 dark:text-slate-400 text-sm sm:text-base leading-relaxed">
            Essential clarity on our engineering engagements, platform architectures, statutory standards, and IP ownership.
          </p>

          {/* Category Filter Chips */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setOpenIdx(null) || setActiveCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  activeCategory === cat
                    ? "bg-brand text-white dark:bg-primary dark:text-slate-950 shadow-sm"
                    : "bg-white dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-brand/40"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Accordion Container */}
        <div className="space-y-3">
          {filteredFaqs.map((faq, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={faq.question}
                className="bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden transition-colors shadow-xs"
              >
                <button
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                  className="w-full px-6 py-4.5 text-left flex items-center justify-between gap-4 cursor-pointer group"
                >
                  <span className="font-bold text-sm sm:text-base text-brand-dark dark:text-slate-100 group-hover:text-brand dark:group-hover:text-primary transition-colors">
                    {faq.question}
                  </span>
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-transform duration-200 ${
                      isOpen
                        ? "rotate-180 bg-primary/20 text-brand dark:text-primary"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-500"
                    }`}
                  >
                    ▼
                  </span>
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 pt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-800/60">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Still Have Questions? Card */}
        <div className="mt-10 p-6 bg-slate-100 dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 rounded-2xl text-center flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-left">
            <h4 className="text-sm font-bold text-brand-dark dark:text-slate-100">
              Require specific architecture scoping or NDA execution?
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Our engineering team responds to project inquiries within 24 business hours.
            </p>
          </div>
          <a
            href="/contact"
            className="px-5 py-2.5 bg-brand dark:bg-primary text-white dark:text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl hover:opacity-90 transition-opacity shrink-0"
          >
            Consult Engineering Desk →
          </a>
        </div>
      </div>
    </section>
  );
}
